import {getLabel} from "../dyanimcFormUtil.tsx";
import React from "react";
import {QCDecision} from "../../../api/ApiTypes.ts";
import {FormComponent, FormType, StaticFieldComponent} from "../DynamicFormTypes.ts";


interface FormChangesModalProps {
    isOpen: boolean;
    differences: Record<string, { old: any; new: any }>;

    // NEW (QC-only)
    comment: string;
    onCommentChange: (comment: string) => void;
    onDecision: (decision: QCDecision) => void;

    // existing
    flattenedSchemaMap: Map<string, FormComponent>;
    simplified?: boolean; // for the simplified version that's temporary
}

interface DatagridFieldChange {
    fieldKey: string;
    old: any;
    new: any;
}

interface DatagridRowChange {
    rowIndex: number;
    fields: DatagridFieldChange[];
}

 // parsing json
const parseFieldValue = (value: any): any => {
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object' && 'label' in parsed) {
                return parsed.label;
            }
        } catch {
            // not JSON, return as-is
        }
    }
    return value;
};

// Diff two datagrid arrays row by row, returning only rows that have changed fields.
const getDifferences = (oldRows: any[], newRows: any[]): DatagridRowChange[] => {
    const maxLen = Math.max(oldRows.length, newRows.length);
    const changedRows: DatagridRowChange[] = [];

    for (let i = 0; i < maxLen; i++) {
        const changedFields = getChangedFields(oldRows[i] ?? {}, newRows[i] ?? {});
        if (changedFields.length > 0) {
            changedRows.push({ rowIndex: i, fields: changedFields });
        }
    }
    return changedRows;
};

const getChangedFields = (oldRow: any, newRow: any): DatagridFieldChange[] => {
    const allKeys = new Set([...Object.keys(oldRow), ...Object.keys(newRow)]);
    const changedFields: DatagridFieldChange[] = [];
    for (const fieldKey of allKeys) {
        const oldVal = parseFieldValue(oldRow[fieldKey]);
        const newVal = parseFieldValue(newRow[fieldKey]);
        if (String(oldVal) !== String(newVal)) {
            changedFields.push({ fieldKey, old: oldVal, new: newVal });
        }
    }
    return changedFields;
};

export const FormChangesModal: React.FC<FormChangesModalProps> = ({
                                                                      isOpen,
                                                                      differences,
                                                                      comment,
                                                                      onCommentChange,
                                                                      onDecision,
                                                                      flattenedSchemaMap,
                                                                      simplified = false,
                                                                  }) => {
    if (!isOpen) return null;

    const differenceCount = Object.keys(differences).length;
    const hasDiffs = differenceCount > 0;

    const formatValue = (value: any): string => {
        if (value === null || value === undefined) {
            return 'empty';
        }
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value, null, 2);
        }
        return String(value);
    };

    const isDatagridKey = (key: string): boolean =>
        flattenedSchemaMap.get(key)?.type === 'datagrid';

    // Separate plain fields from datagrid fields
    const plainDifferences: Record<string, { old: any; new: any }> = {};
    const datagridDifferences: Array<{ key: string; rows: DatagridRowChange[] }> = [];

    const IMAGE_LIST_KEYS = new Set();
    // const OMIT_LIST_KEYS = new Set();
    for (const component of flattenedSchemaMap.values()) {
        if (component.type == FormType.EXPANDED_IMAGE_UPLOAD) {
            IMAGE_LIST_KEYS.add(component.fieldKey);
        }
        if (component.type == FormType.STATIC_FIELD) {
            const staticComponent = component as StaticFieldComponent;
            if (staticComponent.watchFieldKey === "subjectPropertyImages") {
                IMAGE_LIST_KEYS.add(staticComponent.fieldKey);
            }
        }
    }

    for (const [key, { old: oldVal, new: newVal }] of Object.entries(differences)) {
        // if (OMIT_LIST_KEYS.has(key)) {continue;}
        if (isDatagridKey(key)) {
            const rows = getDifferences(oldVal ?? [], newVal ?? []);
            if (rows.length > 0) {
                datagridDifferences.push({ key, rows });
            }
        }
        else if (IMAGE_LIST_KEYS.has(key)) {
            const sortedOld = oldVal ? [...oldVal].sort((a, b) => a.generalFileId - b.generalFileId) : [];
            const sortedNew = newVal ? [...newVal].sort((a, b) => a.generalFileId - b.generalFileId) : [];

            if (sortedOld.length === 0 && sortedNew.length === 0) continue;

            const rows = getDifferences(sortedOld, sortedNew);
            if (rows.length > 0) {
                datagridDifferences.push({ key, rows });
            }
        }
        else {
            plainDifferences[key] = { old: oldVal, new: newVal };
        }
    }

    const datagridCount: number = datagridDifferences.reduce((sum, { rows }) => sum + rows.length, 0);
    const totalDifferences: number = Object.keys(plainDifferences).length + datagridCount;

    const FieldDiff = ({ label, oldVal, newVal }: {
        label: string; oldVal: any; newVal: any; small?: boolean;
    }) => (
        <div className={"bg-gray-50 rounded-md p-4 border border-gray-200"}>
            <div className={"font-medium text-gray-900 text-xs mb-1"}>
                {label}
            </div>
            <div className="space-y-1">
                <div className="flex items-start space-x-2">
                    <span className={"text-red-600 font-medium min-w-[40px] text-xs"}>Old:</span>
                    <span className={"text-gray-700 break-all text-xs"}>{formatValue(oldVal)}</span>
                </div>
                <div className="flex items-start space-x-2">
                    <span className={"text-green-600 font-medium min-w-[40px] text-xs"}>New:</span>
                    <span className={"text-gray-700 break-all text-xs"}>{formatValue(newVal)}</span>
                </div>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">
                        QC Review
                    </h2>
                    <p className="mt-1 text-sm text-gray-600">
                        {simplified
                            ? 'Are you sure you want to submit your QC decision?'
                            : hasDiffs
                                ? `You have made ${totalDifferences} change${differenceCount !== 1 ? 's' : ''} to the form. Please review before accepting or rejecting.`
                                : 'No changes were detected. Choose Accept or Reject, or Cancel.'
                        }
                    </p>
                    {/*<p className="mt-1 text-sm text-gray-600">*/}
                    {/*    {hasDiffs ? (*/}
                    {/*        <>*/}
                    {/*            You have made {totalDifferences} change{differenceCount !== 1 ? 's' : ''} to the form.*/}
                    {/*            Please review before accepting or rejecting.*/}
                    {/*        </>*/}
                    {/*    ) : (*/}
                    {/*        <>*/}
                    {/*            No changes were detected. Choose Accept or Reject, or Cancel.*/}
                    {/*        </>*/}
                    {/*    )}*/}
                    {/*</p>*/}
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto px-4 py-4">
                    {/* Differences (only show when present) */}
                    {hasDiffs && !simplified && (
                        <div className="space-y-3">
                            {/* Plain field changes */}
                            {Object.entries(plainDifferences).map(([key, { old: oldVal, new: newVal }]) => (
                                <FieldDiff
                                    key={key}
                                    label={getLabel(key, flattenedSchemaMap) || key}
                                    oldVal={oldVal}
                                    newVal={newVal}
                                />
                            ))}

                            {/* Datagrid changes — grouped by row */}
                            {datagridDifferences.map(({ key, rows }) =>
                                rows.map(({ rowIndex, fields }) => {
                                    const isImage = IMAGE_LIST_KEYS.has(key);
                                    return (
                                        <div
                                            key={`${key}.${rowIndex}`}
                                            className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                                        >
                                            <div className="font-semibold text-gray-900 mb-2 text-sm">
                                                {getLabel(key, flattenedSchemaMap)}
                                            </div>

                                            {isImage ? (
                                                // All changed fields in one compact block
                                                <div className="space-y-1">
                                                    {fields.map((field) => (
                                                        <div key={field.fieldKey} className="flex items-center gap-2 text-xs">
                                                            <span className="text-gray-500 w-24 shrink-0">{field.fieldKey}</span>
                                                            <span className="flex items-center gap-1">
                                                            <span className="text-red-600 break-all">{formatValue(field.old)}</span>
                                                            <span className="text-gray-400">→</span>
                                                            <span className="text-green-600 break-all">{formatValue(field.new)}</span>
                                                        </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                // Existing datagrid rendering
                                                <div className="space-y-3 pl-2 border-l-2 border-gray-300">
                                                    {fields.map((field) => (
                                                        <FieldDiff
                                                            key={field.fieldKey}
                                                            label={getLabel(field.fieldKey, flattenedSchemaMap)}
                                                            oldVal={field.old}
                                                            newVal={field.new}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}

                    {/* Comment box (always shown in QC) */}
                    <div className={`mt-4 ${hasDiffs ? '' : ''}`}>
                        <label className="block text-sm font-medium text-gray-900">
                            Comment
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => onCommentChange(e.target.value)}
                            rows={4}
                            className="mt-1 w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            placeholder="Add a QC comment (recommended for audit trail)…"
                        />
                    </div>
                </div>

                {/* Footer with buttons */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                    <button
                        onClick={() => onDecision(QCDecision.CANCEL)}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onDecision(QCDecision.REJECT)}
                        className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                        Reject
                    </button>
                    <button
                        onClick={() => onDecision(QCDecision.ACCEPT)}
                        className="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                        Accept
                    </button>
                </div>
            </div>
        </div>
    );
};